import React, { useState, useEffect, useRef } from 'react'
import { 
  Tv, 
  Plus, 
  Search, 
  MoreVertical,
  Pencil,
  Trash2,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Star,
  Hash,
  TrendingUp
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import CreateChannelModal from './CreateChannelModal'
import EditChannelModal from './EditChannelModal'
import ChannelInitializer from '@/components/UI/ChannelInitializer'
import channelService from '@/utils/services/ChannelService.js'
import { Toaster, toast } from 'react-hot-toast'
import { hasAdminPrivileges } from '@/utils/adminUtils'

const Channels = () => {
  const { currentUserData } = useAuth()
  const [channels, setChannels] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingChannel, setEditingChannel] = useState(null)
  const [deletingChannel, setDeletingChannel] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  
  const isAdmin = hasAdminPrivileges(currentUserData)
  
  // Debug logging
  console.log('🔍 Channels component - currentUserData:', currentUserData)
  console.log('🔍 Channels component - isAdmin:', isAdmin)
  
  const fetchChannels = async () => {
    try {
      setLoading(true)
      
      // Initialize channels collection if it doesn't exist
      try {
        await channelService.initializeChannelsCollection()
      } catch (initError) {
        console.warn('Failed to initialize channels collection:', initError)
        // Continue anyway, the collection might already exist
      }
      
      const channelsData = await channelService.getAllChannels()
      setChannels(channelsData)
    } catch (error) {
      console.error('Error fetching channels:', error)
      toast.error('Failed to load channels')
    } finally {
      setLoading(false)
    }
  }
  
  // Load channels on component mount and set up real-time listener
  useEffect(() => {
    fetchChannels()
    
    // Set up real-time listener for channels
    const unsubscribe = channelService.subscribeToChannels((updatedChannels, changes) => {
      console.log('🔄 Channels real-time update received:', changes)
      console.log('📊 Current channels count:', updatedChannels.length)
      
      // Update local state with real-time changes
      setChannels(prevChannels => {
        let newChannels = [...prevChannels]
        
        changes.forEach(change => {
          if (change.type === 'added') {
            console.log('➕ Channel added:', change.doc.name)
            // Add new channel if it doesn't exist
            if (!newChannels.find(c => c.key === change.doc.key)) {
              newChannels.push(change.doc)
            }
          } else if (change.type === 'modified') {
            console.log('✏️ Channel modified:', change.doc.name)
            // Update existing channel
            newChannels = newChannels.map(channel => 
              channel.key === change.doc.key ? change.doc : channel
            )
          } else if (change.type === 'removed') {
            console.log('🗑️ Channel removed:', change.doc.key)
            // Remove deleted channel
            newChannels = newChannels.filter(channel => channel.key !== change.doc.key)
          }
        })
        
        // Maintain sort order
        return newChannels.sort((a, b) => (a.number || 0) - (b.number || 0))
      })
    })
    
    // Cleanup listener on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [])
  
  const filteredChannels = channels.filter(channel => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      return channel.name?.toLowerCase().includes(searchLower) ||
             channel.description?.toLowerCase().includes(searchLower)
    }
    return true
  })
  
  const handleCreateChannel = async (channelData) => {
    try {
      const newChannel = await channelService.createChannel(channelData)
      toast.success('Channel created successfully!')
      setShowCreateModal(false)
      // Update local state instead of re-fetching, maintaining sort order
      setChannels(prevChannels => {
        const updatedChannels = [...prevChannels, newChannel]
        return updatedChannels.sort((a, b) => (a.number || 0) - (b.number || 0))
      })
    } catch (error) {
      toast.error(error.message || 'Failed to create channel')
    }
  }

  const handleEditChannel = async (channelId, channelData) => {
    try {
      const updatedChannel = await channelService.updateChannel(channelId, channelData)
      toast.success('Channel updated successfully!')
      setEditingChannel(null)
      // Update local state instead of re-fetching
      setChannels(prevChannels => 
        prevChannels.map(channel => 
          channel.key === channelId ? { ...channel, ...updatedChannel } : channel
        )
      )
    } catch (error) {
      toast.error(error.message || 'Failed to update channel')
    }
  }

  const handleDeleteChannel = async () => {
    if (!deletingChannel) return
    
    try {
      setIsDeleting(true)
      await channelService.deleteChannel(deletingChannel.key)
      toast.success('Channel deleted successfully!')
      setDeletingChannel(null)
      // Update local state instead of re-fetching
      setChannels(prevChannels => 
        prevChannels.filter(channel => channel.key !== deletingChannel.key)
      )
    } catch (error) {
      toast.error('Failed to delete channel')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleToggleActive = async (channel) => {
    // Optimistic update - update UI immediately
    const originalChannels = [...channels]
    setChannels(prevChannels => 
      prevChannels.map(c => 
        c.key === channel.key ? { ...c, isActive: !c.isActive } : c
      )
    )
    
    try {
      await channelService.toggleChannelActive(channel.key, !channel.isActive)
      toast.success(`Channel ${channel.isActive ? 'deactivated' : 'activated'} successfully!`)
    } catch (error) {
      // Revert on error
      setChannels(originalChannels)
      toast.error('Failed to update channel status')
    }
  }

  const handleToggleMute = async (channel) => {
    // Optimistic update - update UI immediately
    const originalChannels = [...channels]
    setChannels(prevChannels => 
      prevChannels.map(c => 
        c.key === channel.key ? { ...c, isMuted: !c.isMuted } : c
      )
    )
    
    try {
      await channelService.toggleChannelMute(channel.key, !channel.isMuted)
      toast.success(`Channel ${channel.isMuted ? 'unmuted' : 'muted'} successfully!`)
    } catch (error) {
      // Revert on error
      setChannels(originalChannels)
      toast.error('Failed to update channel mute status')
    }
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Tv className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">Admin Access Required</h2>
          <p className="text-gray-500">You need admin privileges to manage channels.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">

      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Live Channels</h1>
          <p className="text-gray-600">Manage your live streaming channels</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Channel
        </button>
      </div>

      {/* Search and Stats */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search channels..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex items-center space-x-4 text-sm text-gray-600">
          <span className="flex items-center">
            <Tv className="w-4 h-4 mr-1" />
            {filteredChannels.length} channel{filteredChannels.length !== 1 ? 's' : ''}
          </span>
          <span className="flex items-center">
            <TrendingUp className="w-4 h-4 mr-1" />
            {filteredChannels.filter(c => c.isActive).length} active
          </span>
        </div>
      </div>

      {/* Channels Grid */}
      <div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading channels...</p>
            </div>
          </div>
        ) : filteredChannels.length === 0 ? (
          <div className="space-y-6">
            <div className="text-center py-12">
              <Tv className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No channels found</h3>
              <p className="text-gray-500">
                {searchTerm ? 'Try adjusting your search.' : 'Get started by creating your first channel.'}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Channel
                </button>
              )}
            </div>
            
            {!searchTerm && (
              <ChannelInitializer />
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredChannels.map((channel) => (
              <ChannelCard
                key={channel.key}
                channel={channel}
                onEdit={() => setEditingChannel(channel)}
                onDelete={() => setDeletingChannel(channel)}
                onToggleActive={() => handleToggleActive(channel)}
                onToggleMute={() => handleToggleMute(channel)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateChannelModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateChannel}
        />
      )}

      {editingChannel && (
        <EditChannelModal
          channel={editingChannel}
          onClose={() => setEditingChannel(null)}
          onSubmit={handleEditChannel}
        />
      )}

      {deletingChannel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Delete Channel</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-6">
              Are you sure you want to delete <strong className="text-gray-900">"{deletingChannel.name}"</strong>?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeletingChannel(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteChannel}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ChannelCard({ channel, onEdit, onDelete, onToggleActive, onToggleMute }) {

  const getActiveStatusIcon = (channel) => {
    return channel.isActive ? 
      <Play className="w-4 h-4 text-green-500" /> : 
      <Pause className="w-4 h-4 text-gray-400" />
  }

  const getActiveStatusText = (channel) => {
    return channel.isActive ? 'Active' : 'Inactive'
  }

  const getActiveStatusColor = (channel) => {
    return channel.isActive ? 'text-green-600 bg-green-100' : 'text-gray-500 bg-gray-100'
  }

  const getMuteStatusIcon = (channel) => {
    return channel.isMuted ? 
      <VolumeX className="w-4 h-4 text-orange-500" /> : 
      <Volume2 className="w-4 h-4 text-green-500" />
  }

  const getMuteStatusText = (channel) => {
    return channel.isMuted ? 'Muted' : 'Unmuted'
  }

  const getMuteStatusColor = (channel) => {
    return channel.isMuted ? 'text-orange-600 bg-orange-100' : 'text-green-600 bg-green-100'
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 hover:border-primary-300 relative">
      {/* Thumbnail */}
      <div className="relative aspect-video bg-gray-100 rounded-t-xl overflow-hidden">
        {channel.thumbnail ? (
          <img
            src={channel.thumbnail}
            alt={channel.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <Tv className="w-12 h-12 text-gray-400" />
          </div>
        )}
        
        {/* Status indicators - stacked vertically on the left */}
        <div className="absolute top-3 left-3 flex flex-col space-y-1">
          {/* Active/Inactive status (always shown) */}
          <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getActiveStatusColor(channel)}`}>
            {getActiveStatusIcon(channel)}
            <span className="ml-1">{getActiveStatusText(channel)}</span>
          </div>
          
          {/* Muted/Unmuted status (always shown) */}
          <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getMuteStatusColor(channel)}`}>
            {getMuteStatusIcon(channel)}
            <span className="ml-1">{getMuteStatusText(channel)}</span>
          </div>
          
          {/* Premium badge (only shown if channel is premium) */}
          {channel.isPremium && (
            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium text-yellow-800 bg-yellow-100 border border-yellow-200">
              <Star className="w-3 h-3 mr-1" />
              Premium
            </div>
          )}
        </div>
        
        {/* Action Icons */}
        <div className="absolute top-3 right-3 flex items-center space-x-1">
          <button
            onClick={() => onEdit()}
            className="p-1.5 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-colors"
            title="Edit Channel"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => onToggleActive()}
            className="p-1.5 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-colors"
            title={channel.isActive ? 'Deactivate Channel' : 'Activate Channel'}
          >
            {channel.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onToggleMute()}
            className="p-1.5 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-colors"
            title={channel.isMuted ? 'Unmute Channel' : 'Mute Channel'}
          >
            {channel.isMuted ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onDelete()}
            className="p-1.5 rounded-full bg-red-600 bg-opacity-80 text-white hover:bg-opacity-100 transition-colors"
            title="Delete Channel"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center">
            <Hash className="w-4 h-4 text-gray-400 mr-1" />
            <span className="text-sm font-medium text-gray-500">#{channel.number}</span>
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <TrendingUp className="w-4 h-4 mr-1" />
            {channel.hits || 0} views
          </div>
        </div>
        
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-1">{channel.name}</h3>
        
        {channel.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{channel.description}</p>
        )}
        
        <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100">
          <span>Created: {new Date(channel.created).toLocaleDateString()}</span>
          <span>Modified: {new Date(channel.modified).toLocaleDateString()}</span>
        </div>
      </div>
      

    </div>
  )
}

export default Channels 